import { useState, useRef, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { useI18n, useL } from "../i18n";

export default function PhotoGallery({ images, alt }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const { t, lang } = useI18n();
  const L = useL();
  const railRef = useRef(null);

  const openAt = (i) => { setIdx(i); setOpen(true); };
  const close = () => setOpen(false);
  const prev = () => setIdx((i) => (i - 1 + (images?.length || 1)) % (images?.length || 1));
  const next = () => setIdx((i) => (i + 1) % (images?.length || 1));

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!images || images.length === 0) return null;

  return (
    <section data-testid="photo-gallery">
      <h2 className="font-display text-2xl text-[#1C1917] mb-5 flex items-center gap-2">
        <Images className="h-5 w-5 text-[#C85A32]" />
        {L({ ar: "المزيد من الصور", en: "More photos", fr: "Plus de photos" })}
      </h2>

      <div
        ref={railRef}
        className="flex gap-3 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-2 -mx-1 px-1"
        data-testid="gallery-rail"
      >
        {images.map((src, i) => (
          <button
            key={i}
            onClick={() => openAt(i)}
            data-testid={`gallery-thumb-${i}`}
            className="shrink-0 snap-start group relative overflow-hidden rounded-2xl w-[70%] sm:w-[45%] md:w-[32%] aspect-[4/3] bg-[#F3EDE4]"
          >
            <img
              src={src}
              alt={`${alt} ${i + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#1C1917]/0 group-hover:bg-[#1C1917]/20 transition-colors" />
          </button>
        ))}
      </div>

      {open && (
        <div
          onClick={close}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-[#1C1917]/95 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <button
            onClick={close}
            data-testid="gallery-close"
            className="absolute top-4 end-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-[#FAF7F2] grid place-items-center"
            aria-label="close"
          >
            <X className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="gallery-prev"
            className="absolute start-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-[#FAF7F2] grid place-items-center"
            aria-label="previous"
          >
            {lang === "ar" ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>

          <img
            onClick={(e) => e.stopPropagation()}
            src={images[idx]}
            alt={`${alt} ${idx + 1}`}
            className="max-h-[85vh] max-w-[92vw] rounded-2xl object-contain"
          />

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="gallery-next"
            className="absolute end-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-[#FAF7F2] grid place-items-center"
            aria-label="next"
          >
            {lang === "ar" ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 text-[#FAF7F2] px-3 py-1 text-xs">
            {idx + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
}
